import { ChevronRightLg } from '@prenuvo/halo-icon';
import { Button, Typography } from '@prenuvo/halo-web';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import { ReactElement, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { ErrorPage } from '@/components';
import { CONFIGURATION_ISSUE } from '@/constants/errorPageCopy';
import config from '@/core/config/config';
import { useGlobalLoading, useLocationPayments } from '@/hooks';
import { usePrenuvoStore } from '@/stores/usePrenuvoStore';
import { Location } from '@/types';

import { getAffirmPaymentType, getStripeToken } from './getStripeToken';

const ErrorPageBody = () => {
  useGlobalLoading(false);

  return (
    <>
      <Typography variant="paragraph-lg" as="p" className="my-6">
        It&apos;s our fault not yours, but don&apos;t worry we can still get you scheduled. Please
        give us a call at{' '}
        <a href="tel:18337736886" className="font-bold text-teal-500 underline">
          {' '}
          1-833-773-6886
        </a>
        , and we&apos;ll be happy to assist you in finalizing your booking.
      </Typography>
      <Button
        size="lg"
        variant="gradient"
        rightIcon={ChevronRightLg}
        onClick={() => window.open(config.BASE_URL)}
      >
        Go to home page
      </Button>
    </>
  );
};

export const PaymentProvider = ({ children }: { children?: ReactElement[] | ReactElement }) => {
  const { locationId, setLocation } = usePrenuvoStore(
    useShallow((state) => ({
      locationId: state.scan.locationId,
      setLocation: state.scan.methods.setLocation,
    })),
  );
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null>>();
  const [showErrorPage, setShowErrorPage] = useState(false);
  const { data, isLoading: isLoadingPayments } = useLocationPayments(locationId);

  useEffect(() => {
    const fetchStripeToken = async () => {
      if (!data) return;
      const stripePublicKey = data && getStripeToken(data);
      if (stripePublicKey) {
        setStripePromise(stripePublicKey);
      } else {
        setShowErrorPage(true);
      }
    };

    const injectAffirmScript = () => {
      if (!data) return;

      const payment = getAffirmPaymentType(data);
      if (payment) {
        const { paymentToken, paymentUrl } = payment;
        if (paymentToken && paymentUrl) {
          const script = document.createElement('script');

          script.innerHTML = `
              _affirm_config = {
                public_api_key:  "${paymentToken}",
                script:          "${paymentUrl}"
              };
              (function(m,g,n,d,a,e,h,c){var b=m[n]||{},k=document.createElement(e),p=document.getElementsByTagName(e)[0],l=function(a,b,c){return function(){a[b]._.push([c,arguments])}};b[d]=l(b,d,"set");var f=b[d];b[a]={};b[a]._=[];f._=[];b._=[];b[a][h]=l(b,a,h);b[c]=function(){b._.push([h,arguments])};a=0;for(c="set add save post open empty reset on off trigger ready setProduct".split(" ");a<c.length;a++)f[c[a]]=l(b,d,c[a]);a=0;for(c=["get","token","url","items"];a<c.length;a++)f[c[a]]=function(){};k.async=
              !0;k.src=g[e];p.parentNode.insertBefore(k,p);delete g[e];f(g);m[n]=b})(window,_affirm_config,"affirm","checkout","ui","script","ready","jsReady");
            `;

          document.head.appendChild(script);
        }
      }
    };

    if (!stripePromise) {
      fetchStripeToken();
    }
    // @ts-ignore
    if (!window.affirm) {
      injectAffirmScript();
    }

    if (data) setLocation(data as Location);
  }, [data, setLocation, stripePromise]);

  if (!stripePromise) {
    if (showErrorPage && !isLoadingPayments) {
      return <ErrorPage {...CONFIGURATION_ISSUE} body={<ErrorPageBody />} />;
    }
    return null; //This is required to prevent runtime error when stripePromise is null before loading but to prevent the error page from being shown for a brief second
  }
  return (
    <Elements
      stripe={stripePromise}
      options={{
        appearance: {
          theme: 'stripe',
          labels: 'floating',
          variables: {
            colorPrimary: '#818181',
            colorText: '#818181',
            colorDanger: '#f04438',
            fontFamily: 'Lato,ui-serif,Georgia,Cambria,Times New Roman,Times,serif',
            borderRadius: '0.75rem',
            fontSizeBase: '16px',
          },
          rules: {},
        },
      }}
    >
      {children}
    </Elements>
  );
};
